import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import {
  FiEye,
  FiClock,
  FiThumbsUp,
  FiMessageCircle,
} from "react-icons/fi";

/* ================= HELPERS ================= */

const formatDuration = (ms = 0) => {
  if (!ms) return "0:00";
  const totalSeconds = Math.floor(ms / 1000);
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
};

/* ================= COMPONENT ================= */

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/client/getVideos")
      .then((res) => {
        if (res.data?.status && Array.isArray(res.data.videos)) {
          setVideos(res.data.videos);
        } else {
          setVideos([]);
        }
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <h3 style={{ textAlign: "center", marginTop: 40 }}>
        Loading videos...
      </h3>
    );
  }

  if (!videos.length) {
    return (
      <h3 style={{ textAlign: "center", marginTop: 40 }}>
        No videos available
      </h3>
    );
  }

  const handleVideoClick = (videoId) => {
    const isUser = window.location.pathname.startsWith("/user");

    navigate(
      isUser
        ? `/user/video/${videoId}`
        : `/video/${videoId}`
    );
  };

  return (
    <div style={styles.container}>
      {videos.map((video) => {
        const thumbnail = video.videoImage || "/no-thumb.png";

        /* ===== BACKEND ALIGNED DATA ===== */
        const views = video.totalViews ?? video.views ?? video.viewsCount ?? 0;
        const likes = video.like ?? video.likes ?? 0;
        const comments = video.totalComments ?? video.comments ?? 0;

        return (
          <div
            key={video._id}
            style={styles.card}
            onClick={() => handleVideoClick(video._id)}
          >
            {/* ===== THUMBNAIL ===== */}
            <div style={{ position: "relative" }}>
              <img
                src={thumbnail}
                alt={video.title}
                style={styles.thumb}
              />

              {/* DURATION */}
              {video.videoTime ? (
                <div style={styles.duration}>
                  <FiClock size={12} />
                  {formatDuration(video.videoTime)}
                </div>
              ) : null}
            </div>

            {/* ===== INFO ===== */}
            <div style={styles.info}>
              <h4 style={styles.title}>{video.title}</h4>

              {/* VIEWS */}
              <div style={styles.metaRow}>
                <FiEye size={13} />
                {views} views
              </div>

              {/* LIKES + COMMENTS */}
              <div style={styles.metaRow}>
                <span style={styles.metaItem}>
                  <FiThumbsUp size={13} /> {likes}
                </span>

                <span style={styles.metaItem}>
                  <FiMessageCircle size={13} /> {comments}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  container: {
    padding: 20,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 20,
  },
  card: {
    cursor: "pointer",
    borderRadius: 10,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  thumb: {
    width: "100%",
    height: 140,
    objectFit: "cover",
    background: "#eee",
  },
  duration: {
    position: "absolute",
    right: 6,
    bottom: 6,
    background: "rgba(0,0,0,0.75)",
    color: "#fff",
    fontSize: 11,
    padding: "2px 6px",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  info: {
    padding: 10,
  },
  title: {
    fontSize: 14,
    margin: "0 0 6px",
    lineHeight: 1.3,
  },
  metaRow: {
    fontSize: 12,
    color: "#777",
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
};
